import { Link } from "react-router-dom";
import { ArrowRight, Check, Plane, Wine, Sparkles, Quote, Star } from "lucide-react";
import { BRAND, SERVICES, TESTIMONIALS } from "../lib/brand";

const heroImageA =
  "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=1400&q=80"; // airplane window — luxury travel
const heroImageB =
  "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=900&q=80"; // fine dining
const heroImageC =
  "https://images.unsplash.com/photo-1694347404068-b435efe29fbb?auto=format&fit=crop&w=900&q=80"; // African American woman in sunglasses — luxury shopping

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* ========== HERO: The Lifestyle Mirror ========== */}
      <section data-testid="hero-section" className="relative overflow-hidden">
        <div className="grid lg:grid-cols-2 min-h-[88vh]">
          {/* LEFT — aspirational lifestyle collage */}
          <div className="relative bg-mint-soft overflow-hidden order-1 lg:order-1">
            <div className="absolute inset-0 grain" />
            <div className="relative h-full grid grid-cols-6 grid-rows-6 gap-3 p-6 sm:p-10">
              <div
                className="col-span-4 row-span-4 rounded-2xl overflow-hidden shadow-xl ring-1 ring-black/5 reveal"
                style={{
                  backgroundImage: `url(${heroImageA})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
                aria-label="First-class travel"
              />
              <div className="col-span-2 row-span-3 rounded-2xl bg-lilac-soft p-5 flex flex-col justify-between shadow-md reveal reveal-d1">
                <Sparkles className="text-lilac-deep" size={22} />
                <div>
                  <div className="font-serif-display text-3xl text-ink leading-none">$612</div>
                  <div className="text-xs uppercase tracking-widest text-charcoal mt-2">
                    grocery run<br />
                    <span className="text-lilac-deep font-semibold">paid $38</span>
                  </div>
                </div>
              </div>
              <div
                className="col-span-2 row-span-3 rounded-2xl overflow-hidden shadow-md reveal reveal-d2"
                style={{
                  backgroundImage: `url(${heroImageC})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <div
                className="col-span-3 row-span-2 rounded-2xl overflow-hidden shadow-md reveal reveal-d3"
                style={{
                  backgroundImage: `url(${heroImageB})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <div className="col-span-3 row-span-2 rounded-2xl bg-white p-5 flex flex-col justify-between shadow-md reveal reveal-d3">
                <Plane className="text-mint-deep" size={20} />
                <div>
                  <div className="font-serif-display text-xl text-ink leading-tight italic">
                    "Our Lisbon trip — funded by groceries."
                  </div>
                  <div className="text-xs text-charcoal mt-2">— Karen J., Chicago</div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — Core promise */}
          <div className="relative flex items-center order-2 lg:order-2 bg-cream">
            <div className="px-6 sm:px-10 lg:px-16 py-16 lg:py-0 max-w-2xl">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-lilac-soft text-lilac-deep text-xs font-semibold tracking-wider uppercase reveal">
                <span className="h-1.5 w-1.5 rounded-full bg-lilac-deep" />
                For women who already earn — and now want the receipts
              </div>

              <h1 className="reveal reveal-d1 mt-7 font-serif-display text-5xl sm:text-6xl lg:text-7xl font-bold text-ink leading-[1.02] tracking-tight">
                Luxury never costs
                <span className="block italic text-lilac-deep">full price.</span>
              </h1>

              <p className="reveal reveal-d2 mt-7 text-lg sm:text-xl text-charcoal leading-relaxed max-w-xl">
                I teach high-earning women the elite art of couponing —
                quietly, tastefully, and without ever clipping a thing in public.
                The money you save? It funds the next first-class seat.
              </p>

              <div className="reveal reveal-d3 mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/book"
                  data-testid="hero-cta-primary"
                  className="btn-primary inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-base font-semibold"
                >
                  Book Your Session <ArrowRight size={18} />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-base font-semibold text-ink border border-ink/15 hover:border-ink/40 transition-colors"
                >
                  See what's inside
                </Link>
              </div>

              <div className="reveal reveal-d3 mt-12 flex items-center gap-5">
                <div className="flex -space-x-3">
                  <div className="h-10 w-10 rounded-full bg-mint border-2 border-cream" />
                  <div className="h-10 w-10 rounded-full bg-lilac border-2 border-cream" />
                  <div className="h-10 w-10 rounded-full bg-mint-soft border-2 border-cream" />
                </div>
                <div className="text-sm text-charcoal">
                  <div className="flex items-center gap-1 text-gold">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={14} fill="currentColor" stroke="none" />
                    ))}
                  </div>
                  <span className="font-medium">Loved by 1,200+ women earning $100k+</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== PAIN-POINT: leaving money on the table ========== */}
      <section
        data-testid="pain-section"
        className="relative bg-ink text-white py-24 sm:py-32 overflow-hidden"
      >
        <div className="absolute inset-0 grain opacity-50" />
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-lilac-deep/30 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-mint-deep/25 blur-3xl" />

        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <p className="font-serif-display italic text-mint text-lg sm:text-xl">
            Let me say this plainly, like Oprah would —
          </p>
          <h2 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]">
            You are leaving
            <span className="block italic shimmer-text">money on the table.</span>
          </h2>
          <p className="mt-8 text-lg sm:text-xl text-white/75 max-w-3xl mx-auto leading-relaxed">
            Every month you spend $1,400 on groceries, $300 on beauty, $250 on
            household — and you tip beautifully because you can. That's not the
            problem. The problem is that <span className="text-mint font-semibold">someone, somewhere,
            is paying $190 for the exact same cart</span> — and it is not because
            she is cheap. It is because she was taught.
          </p>

          <div className="mt-14 grid sm:grid-cols-3 gap-4 sm:gap-6 text-left">
            {[
              { stat: "$8,400", label: "average overspend per household per year" },
              { stat: "92%", label: "of high-earners have never been shown the system" },
              { stat: "1 hour", label: "weekly to redirect that money to your real life" },
            ].map((s, i) => (
              <div
                key={i}
                data-testid={`pain-stat-${i}`}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm"
              >
                <div className="font-serif-display text-4xl text-mint">{s.stat}</div>
                <div className="text-sm text-white/70 mt-2 leading-relaxed">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="mt-14">
            <Link
              to="/services"
              data-testid="pain-cta"
              className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 bg-mint text-ink font-semibold hover:bg-mint/85 transition-colors"
            >
              Show me what I've been missing <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ========== PREMIUM PRODUCT CARDS ========== */}
      <section data-testid="services-preview" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <p className="text-sm uppercase tracking-[0.2em] text-lilac-deep font-semibold">
                The Offerings
              </p>
              <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold text-ink leading-[1.05]">
                Two ways to begin —
                <span className="italic"> both quietly powerful.</span>
              </h2>
            </div>
            <p className="text-charcoal max-w-md leading-relaxed">
              Neither feels like couponing. Both feel like the strategy your richest
              friend would tell you over Sunday brunch — if she trusted you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {SERVICES.map((s, i) => (
              <article
                key={s.id}
                data-testid={`service-card-${s.id}`}
                className={`group relative rounded-3xl overflow-hidden border ${
                  i === 0 ? "border-lilac bg-white" : "border-mint bg-mint-soft/50"
                } p-8 sm:p-10 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1`}
              >
                <div className="absolute top-6 right-6 px-3 py-1 rounded-full bg-ink text-white text-[10px] uppercase tracking-widest font-semibold">
                  {s.badge}
                </div>

                <div className="flex items-baseline gap-3">
                  <span className="font-serif-display text-5xl sm:text-6xl font-bold text-ink">
                    {s.priceLabel}
                  </span>
                </div>
                <div className="text-xs uppercase tracking-widest text-charcoal mt-1">
                  {s.priceNote}
                </div>

                <h3 className="mt-8 font-serif-display text-2xl sm:text-3xl font-bold text-ink leading-tight">
                  {s.name}
                </h3>
                <p className="mt-4 text-charcoal leading-relaxed">{s.tagline}</p>

                <ul className="mt-7 space-y-3">
                  {s.bullets.slice(0, 3).map((b, idx) => (
                    <li key={idx} className="flex gap-3 text-sm text-ink/85">
                      <Check className="shrink-0 text-mint-deep mt-0.5" size={16} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-9 flex items-center justify-between gap-4 pt-6 border-t border-ink/10">
                  <span className="text-xs uppercase tracking-widest text-charcoal">
                    {s.duration}
                  </span>
                  <Link
                    to="/book"
                    data-testid={`service-card-cta-${s.id}`}
                    className="btn-primary inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold"
                  >
                    Book this <ArrowRight size={15} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FOUNDER ========== */}
      <section data-testid="founder-section" className="py-24 sm:py-32 bg-lilac-soft/60">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-2 relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5 aspect-[4/5] max-w-md mx-auto">
              <img
                src={BRAND.founderImage}
                alt="Toni — founder of Six Figure Couponer"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="hidden lg:block absolute -bottom-6 -right-6 bg-mint rounded-2xl px-5 py-4 shadow-xl">
              <div className="font-serif-display text-lg italic text-ink">Hi, I'm Toni.</div>
              <div className="text-xs text-charcoal">Founder · Six Figure Couponer</div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <p className="text-sm uppercase tracking-[0.2em] text-lilac-deep font-semibold">
              The Woman Behind The Method
            </p>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl font-bold text-ink leading-[1.08]">
              I built this for the woman
              <span className="italic"> I used to be.</span>
            </h2>
            <div className="mt-6 space-y-5 text-charcoal text-lg leading-relaxed">
              <p>
                I made six figures and still felt the quiet sting of paying retail. So I
                spent four years building a system that lets women like us save thousands
                a year — without ever, not once, feeling small at the register.
              </p>
              <p className="italic text-ink">
                What couponing built for my family: two paid-off cars, a Lisbon
                summer, our daughter's tuition, and the freedom to walk away from
                anything that no longer fits.
              </p>
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/book"
                data-testid="founder-cta"
                className="btn-primary inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-base font-semibold"
              >
                Sit with me for 30 minutes <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIALS ========== */}
      <section data-testid="testimonials-section" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-sm uppercase tracking-[0.2em] text-lilac-deep font-semibold">
              Real Women. Real Receipts.
            </p>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl font-bold text-ink leading-[1.08]">
              The kind of testimonials
              <span className="italic"> you can feel.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {TESTIMONIALS.map((t, i) => (
              <figure
                key={i}
                data-testid={`testimonial-${i}`}
                className={`relative rounded-3xl p-8 sm:p-10 ${
                  i === 1 ? "bg-ink text-white" : "bg-white border border-border"
                }`}
              >
                <Quote
                  className={`${
                    i === 1 ? "text-mint" : "text-lilac-deep"
                  } mb-5`}
                  size={28}
                />
                <blockquote
                  className={`font-serif-display text-xl leading-snug ${
                    i === 1 ? "text-white" : "text-ink"
                  }`}
                >
                  "{t.quote}"
                </blockquote>
                <figcaption
                  className={`mt-7 pt-6 border-t ${
                    i === 1 ? "border-white/15" : "border-border"
                  }`}
                >
                  <div className={`font-semibold ${i === 1 ? "text-white" : "text-ink"}`}>
                    {t.name}
                  </div>
                  <div
                    className={`text-sm mt-0.5 ${
                      i === 1 ? "text-white/60" : "text-charcoal"
                    }`}
                  >
                    {t.role} · {t.location}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section data-testid="final-cta" className="pb-24 sm:pb-32">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="relative rounded-[2rem] overflow-hidden bg-mint p-10 sm:p-16 text-center">
            <div className="absolute inset-0 grain opacity-50" />
            <div className="relative">
              <Wine className="mx-auto text-ink mb-6" size={32} />
              <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold text-ink leading-[1.05]">
                The next round of luxury
                <span className="block italic">is already discounted.</span>
              </h2>
              <p className="mt-6 text-lg text-ink/75 max-w-2xl mx-auto">
                Thirty minutes. One conversation. A clear plan to redirect what
                you're already spending into what you actually want.
              </p>
              <div className="mt-10">
                <Link
                  to="/book"
                  data-testid="final-cta-btn"
                  className="btn-primary inline-flex items-center gap-2 rounded-full px-8 py-4 text-base font-semibold"
                >
                  Book my session <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
